// Signup page — posts to /api/v1/auth/signup. Mirrors the field
// shape of the predecessor codebase (first_name / last_name /
// broker_name / email / password). On success the user is bounced
// to /login: signup does not auto-issue a session cookie.

import { useMutation } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { AuthShell, signup, useAuth } from "@/features/auth";


type Detail =
  | "personal_email_domain"
  | "weak_password"
  | "invalid_email"
  | "missing_fields"
  | "email_taken";


function messageFor(detail: Detail | string | null): string {
  switch (detail) {
    case "personal_email_domain":
      return "Use your work email — personal addresses (Gmail, Yahoo, etc.) aren't accepted.";
    case "weak_password":
      return "Password must be at least 10 characters.";
    case "invalid_email":
      return "That doesn't look like a valid email address.";
    case "email_taken":
      return "An account with that email already exists. Try logging in instead.";
    case "missing_fields":
      return "Please fill in every field.";
    default:
      return "Something went wrong creating your account. Try again in a moment.";
  }
}


export function Signup() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [brokerName, setBrokerName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Authed visitors skip the form. useEffect fires the redirect;
  // the early-return (below all hook calls — required by the rules
  // of hooks) keeps the form from rendering during the same render.
  useEffect(() => {
    if (auth.status === "authenticated") {
      navigate("/dashboard", { replace: true });
    }
  }, [auth.status, navigate]);

  const mutation = useMutation({
    mutationFn: signup,
    onSuccess: () => {
      navigate("/login", {
        replace: true,
        state: { signupSuccess: true },
      });
    },
  });

  if (auth.status === "authenticated") {
    return null;
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      broker_name: brokerName.trim(),
      email: email.trim(),
      password,
    });
  }

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) {
      const detail =
        (mutation.error.body as { detail?: string } | null)?.detail ?? null;
      return messageFor(detail);
    }
    return messageFor(null);
  })();

  return (
    <AuthShell layout="centered" testId="signup-page">
      <header className="flex flex-col gap-1">
        <h1
          data-testid="signup-title"
          className="text-xl font-semibold tracking-tight text-text-primary"
        >
          Create your account
        </h1>
        <p className="text-sm text-text-secondary">
          Set up your brokerage workspace.
        </p>
      </header>

      <form
        className="mt-6 flex flex-col gap-4"
        onSubmit={onSubmit}
        data-testid="signup-form"
        noValidate
      >
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-sm font-medium text-text-primary">
              First name
            </span>
            <input
              type="text"
              autoComplete="given-name"
              required
              data-testid="signup-first-name"
              className="mt-1 w-full rounded-md border border-border-default bg-raised px-3 py-2 text-base text-text-primary outline-none transition-fast ease-out focus:border-teal focus:shadow-focus"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-text-primary">
              Last name
            </span>
            <input
              type="text"
              autoComplete="family-name"
              required
              data-testid="signup-last-name"
              className="mt-1 w-full rounded-md border border-border-default bg-raised px-3 py-2 text-base text-text-primary outline-none transition-fast ease-out focus:border-teal focus:shadow-focus"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </label>
        </div>

        <label className="block">
          <span className="block text-sm font-medium text-text-primary">
            Brokerage name
          </span>
          <input
            type="text"
            autoComplete="organization"
            required
            data-testid="signup-broker-name"
            placeholder="Acme Brokers"
            className="mt-1 w-full rounded-md border border-border-default bg-raised px-3 py-2 text-base text-text-primary outline-none transition-fast ease-out placeholder:text-text-tertiary focus:border-teal focus:shadow-focus"
            value={brokerName}
            onChange={(e) => setBrokerName(e.target.value)}
          />
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-text-primary">
            Work email
          </span>
          <input
            type="email"
            autoComplete="email"
            required
            data-testid="signup-email"
            placeholder="you@brokerage.com"
            className="mt-1 w-full rounded-md border border-border-default bg-raised px-3 py-2 text-base text-text-primary outline-none transition-fast ease-out placeholder:text-text-tertiary focus:border-teal focus:shadow-focus"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-text-primary">
            Password
          </span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            data-testid="signup-password"
            className="mt-1 w-full rounded-md border border-border-default bg-raised px-3 py-2 text-base text-text-primary outline-none transition-fast ease-out focus:border-teal focus:shadow-focus"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="mt-1 block text-xs text-text-tertiary">
            At least 10 characters.
          </span>
        </label>

        {errorMessage && (
          <div
            role="alert"
            data-testid="signup-error"
            className="rounded-md border border-ember/20 bg-ember-tint px-3 py-2 text-sm text-ember-ink"
          >
            {errorMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={mutation.isPending}
          data-testid="signup-submit"
          className="mt-2 w-full rounded-md bg-teal px-4 py-2.5 text-base font-medium text-text-inverse transition-base ease-out hover:opacity-90 focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-50"
        >
          {mutation.isPending ? "Creating account…" : "Sign up"}
        </button>
      </form>

      <p className="mt-6 border-t border-border-subtle pt-4 text-xs text-text-tertiary">
        Already have an account?{" "}
        <Link
          to="/login"
          data-testid="signup-login-link"
          className="font-medium text-teal hover:underline"
        >
          Log in
        </Link>
        .
      </p>
    </AuthShell>
  );
}
